import React, { useEffect, useState } from 'react';
import { AppBar, Box, Container, Stack, Tab, Tabs, Toolbar, Typography } from '@mui/material';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { ConnectBar } from './_shared/wallet-app/ConnectBar';
import BridgeLanding from './pages/BridgeLanding';
import BridgeStatusPage from './pages/BridgeStatusPage';
import BridgeHistoryPage from './pages/BridgeHistoryPage';
import { BRAND, GRADIENT } from './theme';

// Pathname-based SPA router (tiny — no react-router dependency).
//   /                 → landing
//   /bridge/<id>      → status
//   /history          → history
function detectView() {
  if (typeof window === 'undefined') return { kind: 'landing' };
  const path = window.location.pathname.replace(/\/+$/, '') || '/';
  if (path === '/' || path === '') return { kind: 'landing' };
  const m = path.match(/^\/bridge\/([^/]+)$/);
  if (m) return { kind: 'status', intentId: m[1] };
  if (path === '/history') return { kind: 'history' };
  return { kind: 'landing' };
}

export default function App() {
  const [view, setView] = useState(detectView());

  useEffect(() => {
    const onPop = () => setView(detectView());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const navigate = (path) => {
    window.history.pushState({}, '', path);
    setView(detectView());
  };

  const tabIndex = view.kind === 'landing' ? 0 : view.kind === 'history' ? 1 : false;

  return (
    <Box minHeight="100vh" display="flex" flexDirection="column">
      <AppBar position="sticky" color="default">
        <Container maxWidth="lg" sx={{ px: { xs: 1.5, sm: 3 } }}>
          <Toolbar disableGutters sx={{ py: 1.5, gap: 2 }}>
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              sx={{ flexGrow: 1, minWidth: 0, cursor: 'pointer' }}
              onClick={() => navigate('/')}
            >
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 1.5,
                  background: GRADIENT,
                  display: 'grid',
                  placeItems: 'center',
                  boxShadow: '0 4px 14px rgba(124,58,237,0.4)',
                }}
              >
                <SwapHorizIcon sx={{ color: '#fff', fontSize: 22 }} />
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', lineHeight: 1 }}>
                  Bridge
                </Typography>
                <Typography variant="caption" sx={{ color: BRAND.textSecondary, lineHeight: 1 }}>
                  bankcoin.capital
                </Typography>
              </Box>
            </Stack>
            <ConnectBar />
          </Toolbar>
          <Tabs
            value={tabIndex}
            onChange={(_, v) => navigate(v === 0 ? '/' : '/history')}
            sx={{ minHeight: 38, borderTop: `1px solid ${BRAND.border}` }}
            TabIndicatorProps={{ sx: { height: 3, borderRadius: '3px 3px 0 0' } }}
          >
            <Tab label="Bridge" sx={{ minHeight: 38, py: 1 }} />
            <Tab label="History" sx={{ minHeight: 38, py: 1 }} />
          </Tabs>
        </Container>
      </AppBar>

      <Container
        component="main"
        maxWidth="lg"
        sx={{ flexGrow: 1, py: { xs: 3, md: 5 }, px: { xs: 1.5, sm: 3 } }}
      >
        {view.kind === 'landing' && (
          <BridgeLanding onSubmitted={(intentId) => navigate(`/bridge/${intentId}`)} />
        )}
        {view.kind === 'status' && (
          <BridgeStatusPage intentId={view.intentId} onBack={() => navigate('/')} />
        )}
        {view.kind === 'history' && (
          <BridgeHistoryPage onOpen={(intentId) => navigate(`/bridge/${intentId}`)} />
        )}
      </Container>

      <Box
        component="footer"
        sx={{
          borderTop: `1px solid ${BRAND.border}`,
          backgroundColor: 'rgba(10,10,20,0.5)',
          backdropFilter: 'blur(10px)',
          py: 3,
          textAlign: 'center',
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="caption" sx={{ color: BRAND.textSecondary }}>
            Bridge moves a kStable between chains at 1:1. Burn-on-source +
            mint-on-destination; collateral stays on the source chain. Global
            supply invariant preserved.{' '}
            <a
              href="https://bankcoin.capital"
              target="_blank"
              rel="noreferrer"
              style={{ color: BRAND.violetLight, textDecoration: 'none' }}
            >
              bankcoin.capital
            </a>
          </Typography>
        </Container>
      </Box>
    </Box>
  );
}
